import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { Text } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../../config/runtime';
import type { JournalEntry } from '../../hooks/useProjectsApi';

type TimeWindow = 'hour' | 'day' | 'week';

interface TimelineEvent {
  id: number;
  agent: string;
  event: string;
  ts: Date;
  itemId: string | null;
  payload: Record<string, unknown>;
}

const EVENT_COLORS: Record<string, string> = {
  item_created: '#2196f3',
  item_completed: '#4caf50',
  item_blocked: '#f44336',
  item_updated: '#ff9800',
  agent_mentioned: '#9c27b0',
  goal_created: '#00bcd4',
  goal_updated: '#00bcd4',
  goal_completed: '#4caf50',
  pr_reviewed: '#3f51b5',
  build_failed: '#f44336',
  mentioned: '#9c27b0',
};

const EVENT_ICONS: Record<string, string> = {
  item_created: 'add_circle',
  item_completed: 'check_circle',
  item_blocked: 'block',
  item_updated: 'edit',
  agent_mentioned: 'person',
  goal_created: 'flag',
  goal_updated: 'update',
  goal_completed: 'flag',
  pr_reviewed: 'rate_review',
  build_failed: 'error',
  mentioned: 'alternate_email',
};

function getEventColor(event: string): string {
  return EVENT_COLORS[event.toLowerCase()] || '#9e9e9e';
}

function getEventIcon(event: string): string {
  return EVENT_ICONS[event.toLowerCase()] || 'event';
}

function formatTimestamp(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

interface TimelineChipProps {
  event: TimelineEvent;
}

function TimelineChip({ event }: TimelineChipProps) {
  const color = getEventColor(event.event);
  const icon = getEventIcon(event.event);

  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Text variant="body2" content={event.event.replace(/_/g, ' ')} />
          <Text variant="caption" content={event.agent} customColor="rgba(255,255,255,0.7)" />
          {event.payload && Object.keys(event.payload).length > 0 && (
            <Text variant="caption" content={JSON.stringify(event.payload)} customColor="rgba(255,255,255,0.5)" sx={{ display: 'block', mt: 0.5, fontSize: 10 }} />
          )}
        </Box>
      }
      arrow
    >
      <Chip
        icon={<span className="material-icons" style={{ fontSize: 16, color }}>{icon}</span>}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Text variant="caption" content={event.agent} />
            <Text variant="caption" content={formatTimestamp(event.ts)} customColor="rgba(255,255,255,0.6)" />
          </Box>
        }
        size="small"
        sx={{
          bgcolor: `${color}20`,
          borderColor: color,
          border: '1px solid',
          '& .MuiChip-icon': {
            color: `${color} !important`,
          },
          '& .MuiChip-label': {
            px: 1,
          },
        }}
      />
    </Tooltip>
  );
}

interface TimelineViewProps {
  agent?: string;
  goalId?: string;
  height?: number;
}

export default function TimelineView({ agent, goalId, height = 200 }: TimelineViewProps) {
  const { session } = useAuth();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('day');
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastEventId = useRef<number>(0);

  const getTimeWindowMs = useCallback((window: TimeWindow): number => {
    switch (window) {
      case 'hour': return 24 * 60 * 60 * 1000; // 24h for "hour" view
      case 'day': return 7 * 24 * 60 * 60 * 1000; // 7d for "day" view
      case 'week': return 30 * 24 * 60 * 60 * 1000; // 30d for "week" view
    }
  }, []);

  const fetchJournal = useCallback(async () => {
    if (!session?.accessToken) return;

    setLoading(true);
    try {
      const baseUrl = config.apiUrl || 'https://api.qwickforge.com';
      const params = new URLSearchParams({ limit: '500' });
      if (agent) params.set('agent', agent);
      if (goalId) params.set('goal_id', goalId);

      const since = new Date(Date.now() - getTimeWindowMs(timeWindow)).toISOString();
      params.set('since', since);

      const response = await fetch(`${baseUrl}/api/journal?${params}`, {
        headers: {
          'Authorization': `Bearer ${session.accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const parsedEvents: TimelineEvent[] = (data.entries || []).map((entry: JournalEntry) => ({
          id: entry.id,
          agent: entry.agent,
          event: entry.event,
          ts: new Date(entry.ts),
          itemId: entry.itemId,
          payload: entry.payload || {},
        }));
        setEvents(parsedEvents.sort((a, b) => b.ts.getTime() - a.ts.getTime()));
        if (parsedEvents.length > 0) {
          lastEventId.current = parsedEvents[0].id;
        }
      }
    } catch (err) {
      console.error('Failed to fetch journal:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.accessToken, agent, goalId, timeWindow, getTimeWindowMs]);

  useEffect(() => {
    fetchJournal();
  }, [fetchJournal]);

  useEffect(() => {
    if (!session?.accessToken) return;

    const baseUrl = config.apiUrl || 'https://api.qwickforge.com';
    const url = new URL('/api/events/stream', baseUrl);

    const eventSource = new EventSource(url.toString());
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setConnected(true);
    };

    eventSource.addEventListener('journal_insert', (e) => {
      try {
        const entry = JSON.parse(e.data) as JournalEntry;
        const event: TimelineEvent = {
          id: entry.id,
          agent: entry.agent,
          event: entry.event,
          ts: new Date(entry.ts),
          itemId: entry.itemId,
          payload: entry.payload || {},
        };

        if ((!agent || event.agent === agent) && (!goalId || entry.goalId === goalId)) {
          setEvents((prev) => {
            if (prev.some((e) => e.id === event.id)) return prev;
            return [event, ...prev];
          });
        }
      } catch (err) {
        console.error('Failed to parse journal event:', err);
      }
    });

    eventSource.addEventListener('heartbeat', () => {
      setConnected(true);
    });

    eventSource.onerror = () => {
      setConnected(false);
      eventSource.close();
      eventSourceRef.current = null;
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
      setConnected(false);
    };
  }, [session?.accessToken, agent, goalId]);

  const filteredEvents = events.filter((event) => {
    const eventAge = Date.now() - event.ts.getTime();
    return eventAge < getTimeWindowMs(timeWindow);
  });

  const groupedByHour = filteredEvents.reduce<Record<string, TimelineEvent[]>>((acc, event) => {
    const hourKey = event.ts.toISOString().slice(0, 13);
    if (!acc[hourKey]) acc[hourKey] = [];
    acc[hourKey].push(event);
    return acc;
  }, {});

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'var(--theme-surface)',
        border: '1px solid var(--theme-border)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Text variant="h6" content="Activity Timeline" />
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: connected ? '#4caf50' : '#9e9e9e',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(['hour', 'day', 'week'] as TimeWindow[]).map((window) => (
            <Chip
              key={window}
              label={window}
              size="small"
              onClick={() => setTimeWindow(window)}
              sx={{
                bgcolor: timeWindow === window ? 'var(--theme-primary)' : 'transparent',
                color: timeWindow === window ? 'white' : 'var(--theme-text)',
                border: '1px solid var(--theme-border)',
                '&:hover': {
                  bgcolor: timeWindow === window ? 'var(--theme-primary)' : 'var(--theme-surface-hover)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          height,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'var(--theme-border)',
            borderRadius: 3,
          },
        }}
      >
        {loading && events.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Text variant="body2" content="Loading timeline..." customColor="var(--theme-text-secondary)" />
          </Box>
        ) : filteredEvents.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Text variant="body2" content="No recent activity" customColor="var(--theme-text-secondary)" />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, pb: 1, minWidth: 'max-content' }}>
            {Object.entries(groupedByHour)
              .sort(([a], [b]) => b.localeCompare(a))
              .map(([hour, hourEvents]) => (
                <Box key={hour} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Text variant="caption" content={hour} customColor="var(--theme-text-secondary)" />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {hourEvents.map((event) => (
                      <TimelineChip key={event.id} event={event} />
                    ))}
                  </Box>
                </Box>
              ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
